import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const api = axios.create({
  baseURL: `${API_URL}/api`,
});

// Helper to get token from storage (with fallback)
const getStoredToken = () => {
  return localStorage.getItem('token') || sessionStorage.getItem('token_backup');
};

const getStoredRefreshToken = () => {
  return localStorage.getItem('refresh_token');
};

// Add auth token to requests
api.interceptors.request.use((config) => {
  const token = getStoredToken();
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// Track if a refresh is in-flight to avoid concurrent refresh calls
let isRefreshing = false;
let refreshSubscribers = [];

const onRefreshed = (newToken) => {
  refreshSubscribers.forEach((cb) => cb(newToken));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (cb) => {
  refreshSubscribers.push(cb);
};

// Handle auth errors — attempt refresh on 401
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.response?.status === 401 && !originalRequest._retry) {
      const errorDetail = error.response?.data?.detail || '';

      // Don't refresh for revoked tokens or explicit auth failures
      if (errorDetail.includes('revoked') || errorDetail.includes('Invalid credentials')) {
        return Promise.reject(error);
      }

      // Only try refresh for expired tokens
      const isExpiredOrInvalid = errorDetail.includes('expired') || errorDetail.includes('Invalid token');
      const refreshToken = getStoredRefreshToken();

      if (isExpiredOrInvalid && refreshToken && !originalRequest.url?.includes('/auth/refresh')) {
        if (isRefreshing) {
          // Wait for the ongoing refresh to complete
          return new Promise((resolve) => {
            addRefreshSubscriber((newToken) => {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              originalRequest._retry = true;
              resolve(api(originalRequest));
            });
          });
        }

        isRefreshing = true;
        originalRequest._retry = true;

        try {
          const res = await axios.post(`${API_URL}/api/auth/refresh`, {
            refresh_token: refreshToken,
          });

          const newAccess = res.data.token;
          const newRefresh = res.data.refresh_token;

          localStorage.setItem('token', newAccess);
          sessionStorage.setItem('token_backup', newAccess);
          localStorage.setItem('refresh_token', newRefresh);

          originalRequest.headers.Authorization = `Bearer ${newAccess}`;
          onRefreshed(newAccess);

          return api(originalRequest);
        } catch (refreshError) {
          // Refresh failed — clear everything and let ProtectedRoute redirect
          localStorage.removeItem('token');
          localStorage.removeItem('refresh_token');
          sessionStorage.removeItem('token_backup');
          refreshSubscribers = [];
          // Dispatch a custom event so AuthContext can react
          window.dispatchEvent(new Event('auth:logout'));
          return Promise.reject(refreshError);
        } finally {
          isRefreshing = false;
        }
      }

      // Fallback: clear tokens for non-refreshable 401s on protected paths
      const currentPath = window.location.pathname;
      const publicPaths = [
        '/login', '/signup', '/reset-password', '/pricing', '/',
        '/privacy', '/terms', '/admin/login', '/admin/setup',
        '/admin/forgot-password', '/admin/reset-password', '/admin/setup-account'
      ];
      const isPublicPath = publicPaths.includes(currentPath) || currentPath.startsWith('/admin/') || currentPath.startsWith('/events/');
      const isPaymentReturn = window.location.search.includes('payment=success') ||
                              window.location.search.includes('session_id=');

      if (!isPublicPath && !currentPath.startsWith('/onboarding') && !isPaymentReturn && isExpiredOrInvalid) {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        sessionStorage.removeItem('token_backup');
      }
    }
    return Promise.reject(error);
  }
);

export default api;
